import { useEffect, useState } from "react";
import { Loader2, PencilLine, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { getCoachCurrentRoadmap, saveCoachRoadmapOverrides } from "../lib/api";
import { sydDateTime } from "../lib/time";

const TEXT_FIELDS = ["current_goal", "calorie_range", "protein_range", "step_range", "training_frequency", "nutrition_advice", "fitness_advice", "mindset_guidance"];
const HABIT_FIELDS = ["sleep_habits", "stress_habits"];
const SHORT = new Set(["calorie_range", "protein_range", "step_range", "training_frequency"]);

const habitsToText = (items) => (items || []).map((h) => [h.title, h.explanation].filter(Boolean).join(" | ")).join("\n");
const textToHabits = (txt) => txt.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [title, ...rest] = l.split(/\s+[|\u2014]\s+/);
  return { title: title.trim(), explanation: rest.join(" | ").trim() };
});

export function CoachEditedBadge() {
  return <span className="ml-2 inline-block rounded-full bg-[#C8965A]/15 text-[#8a6234] text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 align-middle" data-testid="coach-edited-badge">Coach-edited</span>;
}

function Field({ k, label, original, multiline, rm, draft, setDraft }) {
  const overridden = rm.overridden_fields.includes(k);
  const Input = multiline ? "textarea" : "input";
  return (
    <div className="py-5 border-t border-brand-ink/10 first:border-t-0" data-testid={`roadmap-edit-field-${k}`}>
      <div className="flex items-center justify-between gap-3">
        <label className="text-sm font-bold text-brand-ink" htmlFor={`rm-${k}`}>{label}{overridden && <CoachEditedBadge />}</label>
        {draft[k] ? (
          <button type="button" onClick={() => setDraft((d) => ({ ...d, [k]: "" }))} className="inline-flex items-center gap-1 text-xs text-brand-ink-muted hover:text-brand-ink" data-testid={`roadmap-revert-${k}`}>
            <RotateCcw size={12} /> Revert to original
          </button>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-brand-ink-muted whitespace-pre-line" data-testid={`roadmap-original-${k}`}>
        Original: {original ? (typeof original === "string" ? original : habitsToText(original)) || "None" : "None"}
      </p>
      <Input id={`rm-${k}`} value={draft[k] || ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
             rows={multiline ? 3 : undefined} placeholder="Leave empty to keep the original value"
             className="mt-2 w-full rounded-xl border border-brand-border bg-white px-4 py-2.5 text-base text-brand-ink focus:outline-none focus:ring-2 focus:ring-[#C8965A]/40"
             data-testid={`roadmap-edit-input-${k}`} />
      {multiline && HABIT_FIELDS.includes(k) && <p className="mt-1 text-xs text-brand-ink-muted">One habit per line as “Title | explanation”.</p>}
    </div>
  );
}


export default function CurrentRoadmapEditor({ clientId, onSaved, onClose }) {
  const [rm, setRm] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCoachCurrentRoadmap(clientId).then((d) => {
      setRm(d);
      const init = {};
      TEXT_FIELDS.forEach((k) => { init[k] = d.overrides[k] || ""; });
      HABIT_FIELDS.forEach((k) => { init[k] = d.overrides[k] ? habitsToText(d.overrides[k]) : ""; });
      setDraft(init);
    }).catch(() => toast.error("Could not load the current Roadmap."));
  }, [clientId]);

  const save = async () => {
    setSaving(true);
    try {
      const fields = {};
      TEXT_FIELDS.forEach((k) => { if (draft[k].trim()) fields[k] = draft[k].trim(); });
      HABIT_FIELDS.forEach((k) => { const h = textToHabits(draft[k] || ""); if (h.length) fields[k] = h; });
      const updated = await saveCoachRoadmapOverrides(clientId, fields);
      setRm(updated);
      toast.success("Current Roadmap saved.");
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (!rm) return <div className="surface-card p-8 mt-6 flex items-center gap-3 text-brand-ink-muted"><Loader2 className="animate-spin" size={18} /> Loading current Roadmap…</div>;

  return (
    <section className="surface-card p-8 mt-6 border-l-4 border-l-[#C8965A]" data-testid="roadmap-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><PencilLine size={14} /> Edit Current Roadmap</div>
          <p className="mt-2 text-sm text-brand-ink-muted max-w-2xl">
            Overrides become the client&apos;s current coaching direction for the live Roadmap view and future Weekly Insights. The original questionnaire answers, calculations and PDF stay untouched. Empty a field to revert to the original.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-brand-ink-muted hover:text-brand-ink" aria-label="Close" data-testid="roadmap-editor-close"><X size={20} /></button>
      </div>
      <div className="mt-4">
        <Field rm={rm} draft={draft} setDraft={setDraft} k="current_goal" label={rm.labels.current_goal} original={rm.original.current_goal} multiline />
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
          {[...SHORT].map((k) => <Field key={k} rm={rm} draft={draft} setDraft={setDraft} k={k} label={rm.labels[k]} original={rm.original[k]} />)}
        </div>
        <Field rm={rm} draft={draft} setDraft={setDraft} k="nutrition_advice" label={rm.labels.nutrition_advice} original={rm.original.nutrition_advice} multiline />
        <Field rm={rm} draft={draft} setDraft={setDraft} k="fitness_advice" label={rm.labels.fitness_advice} original={rm.original.fitness_advice} multiline />
        <Field rm={rm} draft={draft} setDraft={setDraft} k="sleep_habits" label={rm.labels.sleep_habits} original={rm.original.sleep_habits} multiline />
        <Field rm={rm} draft={draft} setDraft={setDraft} k="stress_habits" label={rm.labels.stress_habits} original={rm.original.stress_habits} multiline />
        <Field rm={rm} draft={draft} setDraft={setDraft} k="mindset_guidance" label={rm.labels.mindset_guidance} original="" multiline />
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button type="button" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="roadmap-save-btn">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Changes
        </button>
        {rm.updated_at && <span className="text-xs text-brand-ink-muted" data-testid="roadmap-updated-at">Last edited {sydDateTime(rm.updated_at)}</span>}
      </div>
    </section>
  );
}
